import type { Invitation } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { ChoiceChip, EmptyState, ListRow, type Moment } from '@beyond-js/ui/react';
import { Panel, Unsent } from '@accounts/ui';

const states = { pending: 'pending', accepted: 'done', revoked: 'stopped' } as const;

/**
 * The invitations of an organization, for its owners and administrators (ACC-16): the open ones by
 * default, each with its address, role, who sent it and its expiry, and **Send again** (a new link,
 * mailed) and **Withdraw** in its row's menu (the family's `ListRow`, its state by `Status`); the accepted and withdrawn ones on request, without an expiry.
 * A deleted person's address is said as a deleted account, never shown. A withdrawal that met an ended
 * session says beside it that it was not sent (`unsent`, the invitation's id).
 */
export function Invitations({
	invitations,
	updated,
	all,
	unsent,
	onAll,
	onResend,
	onWithdraw
}: {
	/** Undefined while they are first read: the heading and its filter are drawn and the list waits */
	invitations?: Invitation[];
	/** When the invitations shown were read, while a later read does not answer (D163); null when not known */
	updated?: Moment | null;
	all: boolean;
	unsent?: string;
	onAll: (all: boolean) => void;
	onResend: (invitation: Invitation) => void;
	onWithdraw: (invitation: Invitation) => void;
}) {
	const { text, date, language } = usePresence();
	return (
		<Panel
			id="invitations"
			title={text('invitations.title')}
			actions={
				<ChoiceChip
					label={text('show')}
					value={all ? 'all' : 'open'}
					options={[
						{ value: 'open', label: text('invitations.show.open') },
						{ value: 'all', label: text('invitations.show.all') }
					]}
					onChange={value => onAll(value === 'all')}
					labels={ChoiceChip.labels[language]}
				/>
			}
			waiting={!invitations}
			updated={invitations ? updated : undefined}
		>
			{!invitations ? null : invitations.length === 0 ? (
				<EmptyState title={text('invitations.none')} frame={false} labels={EmptyState.labels[language]} />
			) : (
				<ul className="bui-rows" data-framed aria-label={text('invitations.title')}>
					{invitations.map(invitation => {
						const address = invitation.erased ? text('invitations.erased') : invitation.email;
						const open = invitation.state === 'pending';
						return (
							<ListRow
								key={invitation.id}
								title={address}
								meta={[
									text(`role.${invitation.role}`),
									invitation.inviter ? text('invitations.by', { name: invitation.inviter }) : null,
									open ? text('invitations.expires', { date: date(invitation.expires) }) : null
								]}
								// The state is the row's mark; words only for the accepted and withdrawn ones shown on request (L2)
								lead={{ state: states[invitation.state] ?? 'unknown', label: open ? null : text(`invitations.${invitation.state}`) }}
								menu={
									open
										? [
												{ label: text('invitations.resend'), onSelect: () => onResend(invitation) },
												{ label: text('invitations.revoke'), onSelect: () => onWithdraw(invitation), tone: 'danger' as const }
											]
										: []
								}
							/>
						);
					})}
				</ul>
			)}
			<Unsent end shown={Boolean(unsent && invitations?.some(invitation => invitation.id === unsent))} />
		</Panel>
	);
}
